import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { Loader2 } from 'lucide-react';

/**
 * Route guard that hides the entire portal behind Clerk sign-in.
 *
 * Wrap protected routes (everything except `/sign-in`) in this component. We
 * use `useAuth` rather than `useUser` so the guard fires as soon as Clerk
 * knows the session state — `useUser` lags by an extra render while user
 * metadata loads.
 *
 * Why not just per-page `<SignedOut>` blocks (which is what the portal had
 * before): those let signed-out users land on each page and render a
 * "Sign in to use X" view — but the page's data hooks, contexts, and
 * provider effects (Org/Project/Env/PE fetches) all still mounted and ran.
 * That's both wasted API surface and an information-leak risk if any of the
 * shared providers cached something across sessions. The guard short-circuits
 * the entire render tree before any of that fires.
 *
 * On a signed-out user we preserve the attempted path via location state so
 * the sign-in page can return them to where they were trying to go.
 */
export const RequireSignedIn = ({ children }: { children: React.ReactNode }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) {
    // Clerk is still hydrating the session from cookies — render a brief
    // spinner instead of flashing the unsigned-in redirect.
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};
